/**
 * Página /vecino/avisos — Sprint 11 v2 S11-V2-09, refactor S12-09.
 *
 * Vista dedicada a los avisos de la Comisión. Consume
 * `GET /vecino/avisos/` que retorna los vigentes a la fecha. El
 * contador de no leídos NO se calcula en esta vista (el bell del header
 * maneja "no leído" via NotificacionVecino). Aquí mostramos todos
 * vigentes en orden cronológico inverso.
 *
 * Sprint 12 S12-09 reemplazó el mock estático por fetch real.
 */
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api/client';
import { listarAvisosVecino } from '../../lib/api/avisos';
import type { AvisoRead } from '../../lib/api/types';
import { Notice, Notices } from '../../components/ui/Notice';
import { EmptyState } from '../../components/ui/EmptyState';
import { InfoNote } from '../../components/ui/InfoNote';
import { timeAviso } from '../../lib/dashboard-helpers';

export function Avisos() {
  const [avisos, setAvisos] = useState<AvisoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listarAvisosVecino()
      .then((data) => {
        if (!cancelado) setAvisos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `No pudimos cargar los avisos (error ${err.status}).`
            : 'No pudimos cargar los avisos. Intenta recargar la página.',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return (
    <div>
      <div className="bg-bg-sunken border-b-[0.5px] border-white/[0.08] px-4 md:px-8 pt-6 pb-5">
        <div className="text-sm text-text-muted uppercase tracking-wide mb-1">
          Avisos
        </div>
        <h1 className="text-2xl font-medium text-text-primary leading-tight">
          Avisos de la Comisión
        </h1>
        <p className="text-base text-text-secondary mt-1">
          {avisos.length === 0
            ? 'Sin avisos publicados.'
            : `${avisos.length} ${avisos.length === 1 ? 'aviso vigente' : 'avisos vigentes'}`}
        </p>
      </div>

      <div className="px-4 md:px-8 py-6 max-w-3xl">
        {loading && (
          <p className="text-sm text-text-muted">Cargando avisos…</p>
        )}
        {error && !loading && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
        {!loading && !error && avisos.length === 0 && (
          <EmptyState
            title="La Comisión no ha publicado avisos por ahora"
            description="Cuando se publique un aviso general del barrio, aparecerá aquí. Los avisos sobre tu casa siguen llegando al ícono de campana del header."
          />
        )}
        {!loading && !error && avisos.length > 0 && (
          <Notices>
            {avisos.map((aviso) => (
              <Notice
                key={aviso.id}
                title={aviso.titulo}
                text={aviso.cuerpo}
                time={timeAviso(aviso)}
                unread={false}
              />
            ))}
          </Notices>
        )}

        {!loading && !error && (
          <div className="mt-6">
            <InfoNote>
              Los avisos operativos sobre tu casa (morosidad, pagos aprobados,
              validaciones pendientes) llegan al ícono de campana del header,
              no aquí.
            </InfoNote>
          </div>
        )}
      </div>
    </div>
  );
}
